import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Phone,
  ArrowRight,
  Globe,
  Smartphone,
  Layers,
  Server,
  Palette,
  TrendingUp,
  Video,
  Cloud,
  Megaphone,
  Sparkles,
  ShieldCheck,
  Clock,
  FileText,
} from 'lucide-react';
import { useEstimator } from './context';
import styles from './ProjectEstimator.module.scss';

// Recognizable SVG icon for WhatsApp
const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.15C10.58 20.15 9.13 19.76 7.86 19.01L7.56 18.83L4.44 19.65L5.27 16.61L5.07 16.29C4.24 14.98 3.81 13.47 3.81 11.91C3.81 7.37 7.5 3.68 12.05 3.68C14.25 3.68 16.32 4.54 17.88 6.1C19.43 7.66 20.29 9.72 20.28 11.92C20.28 16.46 16.59 20.15 12.05 20.15ZM16.57 14.39C16.32 14.26 15.1 13.66 14.87 13.58C14.64 13.5 14.48 13.45 14.31 13.7C14.15 13.95 13.68 14.51 13.54 14.67C13.4 14.83 13.26 14.85 13.01 14.73C12.76 14.6 11.96 14.34 11.01 13.49C10.27 12.83 9.77 12.02 9.63 11.77C9.49 11.52 9.61 11.39 9.74 11.26C9.85 11.15 9.99 10.97 10.11 10.83C10.24 10.69 10.28 10.59 10.36 10.42C10.45 10.25 10.4 10.11 10.34 9.98C10.28 9.85 9.79 8.65 9.58 8.16C9.38 7.68 9.18 7.74 9.03 7.73H8.56C8.39 7.73 8.13 7.79 7.9 8.04C7.67 8.29 7.03 8.89 7.03 10.11C7.03 11.33 7.92 12.51 8.04 12.67C8.16 12.83 9.78 15.34 12.27 16.41C12.86 16.67 13.32 16.82 13.68 16.93C14.28 17.12 14.82 17.1 15.25 17.03C15.74 16.96 16.74 16.42 16.95 15.83C17.16 15.24 17.16 14.74 17.1 14.63C17.04 14.52 16.82 14.52 16.57 14.39Z" />
  </svg>
);

const SERVICE_ICONS: Record<string, React.ReactNode> = {
  website: <Globe size={18} />,
  'android-app': <Smartphone size={18} />,
  'android-ios-app': <Layers size={18} />,
  'server-hosting': <Server size={18} />,
  'logo-design': <Palette size={18} />,
  seo: <TrendingUp size={18} />,
  'ugc-video': <Video size={18} />,
  'cloud-solutions': <Cloud size={18} />,
  'digital-marketing': <Megaphone size={18} />,
};

export const EstimateStep: React.FC = () => {
  const { estimate, prevStep, closeEstimator } = useEstimator();

  return (
    <div className={styles.stepContainer}>
      {/* ── Main Investment Showcase Card ─────────────────── */}
      <motion.div
        className={styles.estimateShowcaseCard}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Ambient Glow Spotlight */}
        <div className={styles.ambientGlow} aria-hidden="true" />

        {/* Top Badges Bar */}
        <div className={styles.showcaseTopBar}>
          <div className={styles.estimateBadge}>
            <span className={styles.pulseDot} />
            <span className={styles.badgeText}>{estimate.title}</span>
          </div>

          <button
            type="button"
            className={styles.modifySelectionPill}
            onClick={prevStep}
            id="estimator-modify-services-top"
          >
            ← Modify Services
          </button>
        </div>

        {/* Massive Starting Amount */}
        <div className={styles.amountDisplayArea}>
          <span className={styles.startingPrefix}>STARTING FROM</span>
          <h1 className={styles.estimateMainAmount}>{estimate.formattedTotal}</h1>
        </div>

        <p className={styles.estimatePrimarySub}>
          This is a rough starting estimate based on the services selected. Final pricing depends on
          your requirements, project scope and discussion with our team.
        </p>

        {/* ── Detailed Breakdown Card ─────────────────────── */}
        <div className={styles.breakdownContainer}>
          <div className={styles.breakdownHeader}>
            <div className={styles.breakdownHeadLeft}>
              <Sparkles size={14} className={styles.breakdownSparkleIcon} />
              <span>SELECTED SERVICES</span>
            </div>
            <span className={styles.breakdownHeadRight}>STARTING PRICE</span>
          </div>

          <div className={styles.breakdownItemsList}>
            {estimate.breakdown.map((item) => {
              const icon = SERVICE_ICONS[item.id] || <Sparkles size={16} />;
              return (
                <div key={item.id} className={styles.breakdownItemRow}>
                  <div className={styles.breakdownItemLeft}>
                    <div className={styles.breakdownItemIcon}>{icon}</div>
                    <div className={styles.breakdownItemInfo}>
                      <span className={styles.breakdownItemTitle}>{item.name}</span>
                      {item.isMonthly && (
                        <span className={styles.breakdownMonthlyBadge}>Monthly Retainer</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.breakdownLineSpacer} />
                  <div className={styles.breakdownItemPrice}>
                    <span>{item.displayPrice}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total Line */}
          <div className={styles.breakdownTotalHighlight}>
            <div className={styles.totalHighlightLeft}>
              <span className={styles.totalMainLabel}>TOTAL STARTING ESTIMATE</span>
              <span className={styles.totalSubNotice}>Based on selected base services</span>
            </div>
            <div className={styles.totalHighlightRight}>
              <span className={styles.totalHighlightAmount}>{estimate.formattedTotal}</span>
            </div>
          </div>
        </div>

        {/* Reassurance Disclaimer */}
        <div className={styles.disclaimerPill}>
          <ShieldCheck size={16} className={styles.disclaimerIcon} />
          <span>
            This is a rough starting estimate, not a quotation. Arkatechs tailors exact milestone
            pricing during our introductory call.
          </span>
        </div>
      </motion.div>

      {/* ── Call To Action Section ────────────────────────── */}
      <motion.div
        className={styles.ctaShowcaseSection}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className={styles.ctaPreEyebrow}>START YOUR CONVERSATION</span>
        <h2 className={styles.ctaPrimaryHeading}>LET'S DISCUSS YOUR PROJECT</h2>
        <p className={styles.ctaPrimaryDescription}>
          The detailed scope, features, complexity and final quotation will be discussed during the call.
        </p>

        {/* 3 Action Buttons */}
        <div className={styles.ctaActionButtonsRow}>
          <a
            href="tel:+917021511094"
            className={styles.ctaSecondaryButton}
            id="cta-call-btn"
          >
            <Phone size={15} />
            <span>CALL</span>
          </a>

          <a
            href="https://wa.me/917021511094"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaSecondaryButton}
            id="cta-whatsapp-btn"
          >
            <WhatsAppIcon size={16} />
            <span>WHATSAPP</span>
          </a>

          <Link
            to="/contact"
            onClick={closeEstimator}
            className={styles.ctaRequirementButton}
            id="cta-send-requirement-btn"
          >
            <span>SEND REQUIREMENT</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default EstimateStep;
